import React, { useEffect, useState, useMemo } from "react";
import { Facebook, Instagram, MessageCircle, ShoppingBag, Sun, Moon } from "lucide-react";
import ConfiguratorPanel from "./ConfiguratorPanel";
import TagPreview from "./TagPreview";
import RoundSteelPreview from "./RoundSteelPreview";
import Catalogo from "./Catalogo";
import Cart from "./Cart";
import RatingChip from "./RatingChip";
import { useCart } from "./cartStore";
import { ICON_OPTIONS } from "./icons";
import {
  COLORS, FONTS, PRICE, ROUND_PRICE, CAT_PRICE, SOCIAL_PROOF, translations,
} from "./animauxTagsData";
import "./App.css";

export default function App() {
  const [lang, setLang] = useState("fr");
  const t = translations[lang] || translations.fr;

  const track = (eventName, params = {}) => {
    if (window.gtag) window.gtag("event", eventName, params);
    if (window.posthog) window.posthog.capture(eventName, params);
  };

  // Theme
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem("animaux_theme") || "dark";
    } catch {
      return "dark";
    }
  });
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem("animaux_theme", theme);
    } catch {}
  }, [theme]);

  const [screen, setScreen] = useState("home");
  const [view, setView] = useState("front");
  const [model, setModel] = useState("hueso");
  const [color, setColor] = useState("negro");
  const [font, setFont] = useState(FONTS[1].value);
  const [icon, setIcon] = useState("none");
  const [petName, setPetName] = useState("Animaux Tags");
  const [phone, setPhone] = useState("");
  const [addr, setAddr] = useState("");
  const [extra, setExtra] = useState("");
  const [design, setDesign] = useState("none");
  const [qty, setQty] = useState(1);

  const cart = useCart();

  const unitPrice = useMemo(() => {
    if (model === "redonda") return ROUND_PRICE;
    if (model === "gatos") return CAT_PRICE;
    return PRICE;
  }, [model]);

  const itemTotal = useMemo(() => {
    let total = unitPrice * qty;
    if (qty === 2) total *= 0.85;
    if (qty >= 3) total *= 0.8;
    return total;
  }, [unitPrice, qty]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const colorMeta = useMemo(() => {
    if (model === "redonda") {
      return color === "oro"
        ? { hex: "#d8b45a", label: t.spec_finish_gold || "Gold" }
        : { hex: "#cfd1d1", label: t.spec_finish_silver || "Silver" };
    }
    return COLORS.find((c) => c.name === color) || { hex: "#2a2a2a", label: color };
  }, [color, model, t]);

  const modelLabel = useMemo(() => {
    if (model === "hueso") return t.model_bone;
    if (model === "patas") return t.model_paw;
    if (model === "redonda") return t.model_round;
    return t.model_cat;
  }, [model, t]);

  const handleAddToCart = () => {
    cart.addItem({
      model,
      modelLabel,
      color,
      colorLabel: colorMeta.label,
      colorHex: colorMeta.hex,
      font,
      petName,
      phone,
      addr,
      extra,
      icon,
      iconLabel: ICON_OPTIONS.find((o) => o.key === icon)?.[`label_${lang}`] || icon,
      design: model === "redonda" ? design : "none",
      qty,
      unitPrice,
    });
    track("add_to_cart", { model, color, qty, value: unitPrice * qty });
  };

  const navItems = [
    ["home", t.nav_home],
    ["create", t.nav_create],
    ["catalog", t.nav_catalog],
    ["photos", t.nav_photos],
    ["reviews", t.nav_reviews],
    ["faq", t.nav_faq],
    ["cart", t.nav_cart],
  ];

  const showStickyBar = screen === "create";

  return (
    <div className="site-shell" data-theme={theme}>
      <aside className="side-nav">
        <img className="brand-logo" src="/logo.png" alt="Animaux Tags" />

        <div className="lang-row">
          {["fr", "en", "es"].map((l) => (
            <button
              key={l}
              onClick={() => {
                setLang(l);
                track("language_change", { language: l });
              }}
              className={lang === l ? "active" : ""}
              aria-label={`Language: ${l.toUpperCase()}`}
            >
              {l.toUpperCase()}
            </button>
          ))}
        </div>

        <nav className="nav-buttons" aria-label="Main navigation">
          {navItems.map(([key, label]) => (
            <button
              key={key}
              onClick={() => {
                setScreen(key);
                track("navigation_click", { screen: key });
              }}
              className={screen === key ? "active" : ""}
              data-testid={`nav-${key}`}
            >
              {key === "cart" ? (
                <span className="nav-cart-row">
                  <ShoppingBag size={16} /> {label}
                  {cart.totalCount > 0 && <span className="cart-badge">{cart.totalCount}</span>}
                </span>
              ) : (
                label
              )}
            </button>
          ))}
        </nav>

        <button
          type="button"
          className="theme-toggle"
          onClick={() => setTheme((th) => (th === "dark" ? "light" : "dark"))}
          aria-label={`Toggle ${theme === "dark" ? "light" : "dark"} theme`}
          data-testid="theme-toggle"
        >
          {theme === "dark" ? <Sun size={16} /> : <Moon size={16} />}
          <span>{t.theme_toggle}: {theme === "dark" ? "Dark" : "Light"}</span>
        </button>

        <div className="social-sidebar">
          <span>{t.social_follow}</span>
          <div>
            <a
              href="https://www.instagram.com/animauxtags"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Instagram"
              onClick={() => track("social_click", { platform: "instagram" })}
            >
              <Instagram size={22} />
            </a>
            <a
              href="https://www.facebook.com/animauxtags/"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Facebook"
              onClick={() => track("social_click", { platform: "facebook" })}
            >
              <Facebook size={22} />
            </a>
            <a
              href="https://www.instagram.com/animauxtags"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Direct message on Instagram"
              onClick={() => track("dm_click", { platform: "instagram" })}
            >
              <MessageCircle size={22} />
            </a>
          </div>
        </div>
      </aside>

      <main className="main-view">
        {screen === "home" && (
          <section className="home-grid">
            <div className="home-copy">
              <span className="pill">{t.free_shipping_msg}</span>
              <h1>{t.hero_title}</h1>
              <RatingChip rating={SOCIAL_PROOF.rating} count={SOCIAL_PROOF.count} label={t.reviews_total} />
              <p>{t.hero_text}</p>
              <div className="cta-row">
                <button
                  className="gold-btn"
                  onClick={() => {
                    setScreen("create");
                    track("create_tag_click", { source: "home" });
                  }}
                  data-testid="hero-cta"
                >
                  {t.hero_cta}
                </button>
                <button
                  className="ghost-btn"
                  onClick={() => {
                    setScreen("photos");
                    track("photos_click", { source: "home" });
                  }}
                >
                  {t.hero_second}
                </button>
              </div>

              <div className="trust-grid">
                <div>{t.trust_1}</div>
                <div>{t.trust_2}</div>
                <div>{t.trust_3}</div>
                <div>{t.trust_4}</div>
              </div>
            </div>

            <div className="hero-card">
              <img src="/img/hero/luna-medalla.webp" alt="Luna avec sa médaille personnalisée" loading="eager" />
            </div>
          </section>
        )}

        {screen === "create" && (
          <section className="creator-layout">
            <div className="preview-zone">
              {/* Persistent price chip + urgency */}
              <div className="preview-toolbar">
                <div className="preview-price-chip">
                  <span className="ppc-label">{t.total_label}</span>
                  <strong data-testid="preview-price">${itemTotal.toFixed(2)}</strong>
                  <small>CAD</small>
                </div>
                <div className="preview-urgency" aria-live="polite">
                  <span className="urgency-dot" /> {SOCIAL_PROOF.weeklyOrders}+ {t.urgency_weekly}
                </div>
                <RatingChip rating={SOCIAL_PROOF.rating} count={SOCIAL_PROOF.count} label={t.reviews_total} compact />
              </div>

              <div className="view-toggle" role="tablist" aria-label="Aperçu">
                <button
                  onClick={() => setView("front")}
                  className={view === "front" ? "active" : ""}
                  data-testid="view-front"
                >
                  {t.view_front}
                </button>
                <button
                  onClick={() => setView("back")}
                  className={view === "back" ? "active" : ""}
                  data-testid="view-back"
                >
                  {t.view_back}
                </button>
              </div>

              {model === "redonda" ? (
                <RoundSteelPreview
                  finish={color === "oro" ? "oro" : "plata"}
                  petName={petName}
                  phone={phone}
                  addr={addr}
                  extra={extra}
                  view={view}
                  t={t}
                  icon={icon}
                  design={design}
                  font={font}
                />
              ) : (
                <TagPreview
                  model={model}
                  color={color}
                  font={font}
                  petName={petName}
                  phone={phone}
                  addr={addr}
                  extra={extra}
                  view={view}
                  icon={icon}
                />
              )}

              <p className="product-note">
                {model === "redonda" ? t.material_round : model === "gatos" ? t.material_cat : t.product_note}
              </p>
              <p className="delivery-line">📦 {t.delivery_estimate}</p>
            </div>

            <ConfiguratorPanel
              t={t}
              lang={lang}
              model={model}
              setModel={setModel}
              color={color}
              setColor={setColor}
              font={font}
              setFont={setFont}
              icon={icon}
              setIcon={setIcon}
              petName={petName}
              setPetName={setPetName}
              phone={phone}
              setPhone={setPhone}
              addr={addr}
              setAddr={setAddr}
              extra={extra}
              setExtra={setExtra}
              design={design}
              setDesign={setDesign}
              qty={qty}
              setQty={setQty}
              unitPrice={unitPrice}
              itemTotal={itemTotal}
              onAddToCart={handleAddToCart}
              setScreen={setScreen}
            />
          </section>
        )}

        {screen === "catalog" && (
          <Catalogo
            t={t}
            lang={lang}
            setScreen={setScreen}
            setModel={setModel}
            setColor={setColor}
            track={track}
          />
        )}

        {screen === "photos" && (
          <section className="page-card">
            <h2>{t.photo_title}</h2>
            <p>{t.photo_text}</p>
            <div className="photo-grid premium-gallery">
              {[
                { src: "/img/hero/luna-medalla.webp", alt: "Luna avec médaille", name: "Luna", caption: "Médaille os rouge personnalisée" },
                { src: "/img/hero/kira-hero.png", alt: "Kira avec médaille", name: "Kira", caption: "Style premium au quotidien" },
                { src: "/img/lifestyle/nala-autumn.png", alt: "Nala avec médaille", name: "Nala", caption: "Visible, légère et élégante" },
                { src: "/img/product/simba-close.png", alt: "Simba avec médaille", name: "Simba", caption: "Gravure claire et durable" },
              ].map((p) => (
                <figure key={p.name}>
                  <div className="gallery-image-box">
                    <img src={p.src} alt={p.alt} loading="lazy" />
                  </div>
                  <figcaption>
                    <strong>{p.name}</strong>
                    <span>{p.caption}</span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </section>
        )}

        {screen === "reviews" && (
          <section className="page-card">
            <div className="reviews-head">
              <h2>{t.reviews_title}</h2>
              <RatingChip rating={SOCIAL_PROOF.rating} count={SOCIAL_PROOF.count} label={t.reviews_total} />
            </div>
            <div className="review-grid">
              {(t.reviews || []).map((review) => (
                <article key={review.name} className="review-card" data-testid={`review-${review.name}`}>
                  <div className="review-photo">
                    <img src={review.photo} alt={`${review.name}`} loading="lazy" />
                  </div>
                  <div className="review-body">
                    <div className="review-stars">{"★".repeat(review.rating || 5)}</div>
                    <strong>{review.name}</strong>
                    <p>{review.text}</p>
                    <small>{review.date}</small>
                  </div>
                </article>
              ))}
            </div>
          </section>
        )}

        {screen === "faq" && (
          <section className="page-card">
            <h2>{t.faq_page_title}</h2>
            <div className="faq-list">
              {(Array.isArray(t.faqs) ? t.faqs : []).map((f) => (
                <details key={f.id}>
                  <summary>{f.q}</summary>
                  <p>{f.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {screen === "cart" && (
          <Cart t={t} lang={lang} setScreen={setScreen} track={track} />
        )}

        {/* Site Footer - only on non-configurator screens */}
        {screen !== "create" && (
          <footer className="site-footer">
            <div className="footer-grid">
              <div className="footer-brand">
                <img src="/logo.png" alt="Animaux Tags" className="footer-logo" />
                <p>{t.hero_text ? t.hero_text.slice(0, 80) + "..." : "Médailles personnalisées premium pour chiens & chats."}</p>
              </div>
              <div className="footer-col">
                <h4>{t.nav_catalog || "Catalogue"}</h4>
                <button onClick={() => setScreen("catalog")}>{t.model_bone || "Os"}</button>
                <button onClick={() => setScreen("catalog")}>{t.model_paw || "Pattes"}</button>
                <button onClick={() => setScreen("catalog")}>{t.model_round || "Ronde"}</button>
                <button onClick={() => setScreen("catalog")}>{t.model_cat || "Chats"}</button>
              </div>
              <div className="footer-col">
                <h4>{t.nav_faq || "Support"}</h4>
                <button onClick={() => setScreen("faq")}>{t.nav_faq || "FAQ"}</button>
                <a href="mailto:animauxtags@gmail.com">animauxtags@gmail.com</a>
                <a href="https://www.instagram.com/animauxtags" target="_blank" rel="noopener noreferrer">Instagram</a>
                <a href="https://www.facebook.com/animauxtags/" target="_blank" rel="noopener noreferrer">Facebook</a>
              </div>
              <div className="footer-col">
                <h4>{lang === "fr" ? "Paiement sécurisé" : lang === "es" ? "Pago seguro" : "Secure Payment"}</h4>
                <div className="footer-trust-badges">
                  <span>VISA</span>
                  <span>Mastercard</span>
                  <span>PayPal</span>
                  <span>Interac</span>
                </div>
                <p className="footer-shipping">
                  {lang === "fr" ? "📦 Livraison gratuite au Canada & USA" : lang === "es" ? "📦 Envío gratis a Canadá & USA" : "📦 Free shipping Canada & USA"}
                </p>
              </div>
            </div>
            <div className="footer-bottom">
              <span>© 2026 Animaux Tags. {lang === "fr" ? "Tous droits réservés." : lang === "es" ? "Todos los derechos reservados." : "All rights reserved."}</span>
              <span>{lang === "fr" ? "🔒 Paiement 100% sécurisé" : lang === "es" ? "🔒 Pago 100% seguro" : "🔒 100% Secure Checkout"}</span>
            </div>
          </footer>
        )}
      </main>

      {/* Sticky mobile CTA on create screen */}
      {showStickyBar && (
        <div className="sticky-mobile-bar" data-testid="sticky-mobile-bar">
          <div className="sticky-info">
            <span className="sticky-label">{t.total_label}</span>
            <strong>${itemTotal.toFixed(2)}</strong>
          </div>
          <button type="button" onClick={handleAddToCart} className="sticky-cta" data-testid="sticky-add-btn">
            {t.sticky_cta}
          </button>
        </div>
      )}
    </div>
  );
}
